import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// Disambiguates the AeroChat support widget (loaded in the admin panel via
// chatbox_live_admin.js) from a merchant's own customer-facing widget. Rendered
// only inside aerochat-admin-ui; merchant customer sites never load this
// component because they don't ship the admin React bundle.
//
// Behavior:
//   • Anchored to the live position of #aerochat-launcher (read every rAF).
//   • Hidden during an active drag of the launcher; reappears on release at
//     the new position.
//   • Hidden while the chat panel is open; reappears when the chat closes.
//   • Hidden while the SupportNudge bubble is visible (also anchored to the
//     launcher; the two would otherwise stack and collide).
//   • Hidden on mobile via the existing CSS media query in layout.css.
//   • Background colour is read from the launcher's computed style on every
//     tick so the chip auto-matches the merchant's brand colour. Text colour
//     flips between dark / light based on background luminance (WCAG).

const LAUNCHER_ID = 'aerochat-launcher';
// Once the chat panel reads "closed" we wait this long before showing the chip
// again. The external chat panel animates open/closed and slides across the
// size threshold in isChatPanelOpen() for several frames, so a raw per-frame
// reading flip-flops; the settle window absorbs that and stops the flicker.
const CLOSE_SETTLE_MS = 280;
// When the launcher is clicked we don't yet know whether the chat is opening
// or closing, and the panel hasn't animated to a detectable size. Suppress the
// chip for this grace period so it doesn't flash between the click and the
// chat-open detection catching up.
const OPEN_GRACE_MS = 700;
const TRANSPARENT_RE = /^(rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)|transparent|)$/i;
const RGB_RE = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;
const DEFAULT_BG = '#1f2937';
const DEFAULT_TEXT = '#ffffff';

// Read the launcher's effective background colour. Tries the launcher itself
// first, then walks first-level children if the launcher is transparent (some
// chatbox builds put the colour on an inner <span> or <svg> wrapper).
function readWidgetColor(launcher) {
  if (!launcher) return null;
  const direct = window.getComputedStyle(launcher).backgroundColor;
  if (direct && !TRANSPARENT_RE.test(direct)) return direct;
  for (const child of launcher.children) {
    const c = window.getComputedStyle(child).backgroundColor;
    if (c && !TRANSPARENT_RE.test(c)) return c;
  }
  return null;
}

// Convert any rgb()/rgba() colour string to { r, g, b, a, rgb, rgba }.
// Returns null on unparseable input. The dual rgb + rgba output ensures the
// caller has a working fallback even if one form fails to apply.
function parseRgb(color) {
  if (!color) return null;
  const m = color.match(RGB_RE);
  if (!m) return null;
  const r = Number(m[1]);
  const g = Number(m[2]);
  const b = Number(m[3]);
  const a = m[4] !== undefined ? Number(m[4]) : 1;
  return {
    r, g, b, a,
    rgb: `rgb(${r}, ${g}, ${b})`,
    rgba: `rgba(${r}, ${g}, ${b}, ${a})`,
  };
}

// WCAG-style luminance check. Returns dark text on light backgrounds so the
// chip stays readable even when the merchant picks a pale brand colour.
function pickTextColor(parsed) {
  if (!parsed) return DEFAULT_TEXT;
  const luminance = (0.299 * parsed.r + 0.587 * parsed.g + 0.114 * parsed.b) / 255;
  return luminance > 0.6 ? '#1f2937' : '#ffffff';
}

// Heuristic chat-panel detector. chatbox_live.js is external code, so we
// can't import a state hook from it. Instead we look for a large enough
// fixed-positioned element near the launcher whose id/class names match
// common chat-widget conventions. If the chat panel ever uses a selector
// outside this set we'll see the chip stay visible behind the chat — at
// which point inspecting the panel and adding its selector here is a one-
// liner.
//
// The real AeroChat support widget (launcher + chat panel) is injected by
// chatbox_live.js into document.body, OUTSIDE the admin React root (#root).
// In-app UI that merely *looks* chat-like — most importantly the live widget
// preview iframe on the "Test your widget" page (title "AeroChat Widget",
// which matches iframe[title*="chat" i]) — lives INSIDE #root. We skip any
// candidate contained in #root so that previewing the merchant's own widget
// no longer false-positives as "support chat open" and hides the chip.
function isChatPanelOpen(launcher) {
  if (!launcher) return false;
  const appRoot = document.getElementById('root');
  const candidates = document.querySelectorAll(
    '[id*="aerochat"]:not(#aerochat-launcher), ' +
    '[class*="chatbox"], ' +
    '[class*="aerochat-chat"], ' +
    'iframe[id*="aerochat"], ' +
    'iframe[title*="chat" i]'
  );
  for (const el of candidates) {
    if (el === launcher || el.contains(launcher)) continue;
    if (appRoot && appRoot.contains(el)) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width >= 200 && rect.height >= 200) return true;
  }
  return false;
}

// SupportNudge renders its bubble with the class support-nudge__bubble
// (see SupportNudge.jsx). When it's present in the DOM we hide our chip
// to avoid stacking two bubbles on the same launcher.
function isSupportNudgeVisible() {
  return !!document.querySelector('.support-nudge__bubble');
}

const WidgetHelpLabel = () => {
  const { t } = useTranslation('widgetContext');

  const [anchor, setAnchor] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [widgetColor, setWidgetColor] = useState(null);
  // Measured width of the rendered chip, used to clamp it inside the viewport
  // so it never gets cut off when the launcher sits in a screen corner.
  const [pillWidth, setPillWidth] = useState(0);
  const pillRef = useRef(null);
  const isDraggingRef = useRef(false);
  // chatOpenRef holds the debounced open state; closedSinceRef timestamps the
  // first "closed" reading so we can require CLOSE_SETTLE_MS of stability
  // before flipping back to visible. suppressUntilRef holds the end of the
  // click grace window (see OPEN_GRACE_MS).
  const chatOpenRef = useRef(false);
  const closedSinceRef = useRef(0);
  const suppressUntilRef = useRef(0);

  // rAF poll loop. A single tick handles position, debounced chat-open state,
  // nudge visibility, widget colour, and the final hidden flag — cheaper than
  // separate observers and matches the pattern used by SupportNudge.jsx.
  // Keeping the chip mounted and toggling `hidden` (instead of unmounting)
  // lets the CSS opacity/transform transition fade it in and out smoothly.
  useEffect(() => {
    let rafId = 0;
    const tick = () => {
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const launcher = document.getElementById(LAUNCHER_ID);
      if (launcher) {
        const rect = launcher.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setAnchor((prev) => {
            if (prev
              && prev.left === rect.left
              && prev.top === rect.top
              && prev.width === rect.width
              && prev.height === rect.height) {
              return prev;
            }
            return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
          });
        } else {
          setAnchor(null);
        }

        // Debounced chat-open detection: report "open" instantly (hide the
        // chip the moment the panel appears), but only report "closed" after
        // the panel has measured closed continuously for CLOSE_SETTLE_MS.
        const rawOpen = isChatPanelOpen(launcher);
        if (rawOpen) {
          closedSinceRef.current = 0;
          chatOpenRef.current = true;
        } else {
          if (closedSinceRef.current === 0) closedSinceRef.current = now;
          if (now - closedSinceRef.current >= CLOSE_SETTLE_MS) {
            chatOpenRef.current = false;
          }
        }

        const nextColor = readWidgetColor(launcher);
        setWidgetColor((prev) => (prev === nextColor ? prev : nextColor));
      } else {
        setAnchor(null);
      }

      // Track the chip's rendered width so the render pass can clamp it inside
      // the viewport. Read here (in the existing tick) to avoid a separate
      // observer; offsetWidth is a single cheap read.
      const w = pillRef.current ? pillRef.current.offsetWidth : 0;
      setPillWidth((prev) => (prev === w ? prev : w));

      const nudgeVisible = isSupportNudgeVisible();
      const suppressed = now < suppressUntilRef.current;
      const nextHidden = isDraggingRef.current || chatOpenRef.current || nudgeVisible || suppressed;
      setHidden((prev) => (prev === nextHidden ? prev : nextHidden));

      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, []);

  // Drag detection. Listening at document level (not on the launcher)
  // because the launcher is rendered by external code and may be replaced
  // / re-parented mid-session. Walking from event.target up to launcher
  // covers clicks inside SVG icons, span labels, etc.
  useEffect(() => {
    const onStart = (e) => {
      const launcher = document.getElementById(LAUNCHER_ID);
      if (!launcher) return;
      const target = e.target;
      if (target === launcher || (target instanceof Node && launcher.contains(target))) {
        isDraggingRef.current = true;
        // A press on the launcher is either the start of a drag or a click to
        // toggle the chat. Either way, hide the chip immediately and keep it
        // hidden through the grace window so it can't flash while the chat
        // panel animates open/closed before isChatPanelOpen() catches up.
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        suppressUntilRef.current = now + OPEN_GRACE_MS;
      }
    };
    const onEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
    };
    document.addEventListener('mousedown', onStart, true);
    document.addEventListener('mouseup', onEnd, true);
    document.addEventListener('touchstart', onStart, { capture: true, passive: true });
    document.addEventListener('touchend', onEnd, true);
    document.addEventListener('touchcancel', onEnd, true);
    return () => {
      document.removeEventListener('mousedown', onStart, true);
      document.removeEventListener('mouseup', onEnd, true);
      document.removeEventListener('touchstart', onStart, true);
      document.removeEventListener('touchend', onEnd, true);
      document.removeEventListener('touchcancel', onEnd, true);
    };
  }, []);

  // Without a positioned launcher there is nothing to anchor to, so we don't
  // render at all. Open/close transitions are handled by the `hidden` class
  // below (the chip stays mounted and fades) rather than by unmounting.
  if (!anchor) return null;

  // Parse the colour into both rgb and rgba forms so a hand-off in either
  // direction still produces a working background. Apply rgb first; some
  // older WebViews mis-handle inline rgba with full alpha — the rgba then
  // serves as a defensive fallback that browsers will overwrite the rgb
  // declaration with where supported.
  const parsed = parseRgb(widgetColor);
  const bgRgb = parsed ? parsed.rgb : DEFAULT_BG;
  const bgRgba = parsed ? parsed.rgba : DEFAULT_BG;
  const textColor = pickTextColor(parsed);

  const anchorCenter = anchor.left + anchor.width / 2;
  const chipTop = anchor.top - 12;

  // Clamp the chip horizontally so it can't be cut off when the launcher sits
  // in a screen corner. The chip is centred on the launcher (translateX(-50%)),
  // so keep its half-width inside the viewport with a small margin. The arrow
  // (::after) is then shifted by the same delta — bounded so it stays on the
  // chip — to keep it pointing at the launcher.
  const VIEWPORT_MARGIN = 8;
  let chipLeft = anchorCenter;
  let arrowDx = 0;
  if (pillWidth > 0 && typeof window !== 'undefined') {
    const half = pillWidth / 2;
    // Use documentElement.clientWidth (not window.innerWidth) for the right
    // edge: clientWidth excludes the vertical scrollbar, so on pages that have
    // one the chip is kept to its left instead of being clipped by it. On pages
    // with no scrollbar the two are equal, so behaviour there is unchanged.
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const minLeft = VIEWPORT_MARGIN + half;
    const maxLeft = viewportWidth - VIEWPORT_MARGIN - half;
    if (maxLeft >= minLeft) {
      chipLeft = Math.min(Math.max(anchorCenter, minLeft), maxLeft);
    }
    const maxArrow = Math.max(0, half - 12);
    arrowDx = Math.max(-maxArrow, Math.min(maxArrow, anchorCenter - chipLeft));
  }

  // NOTE: the base transform (translate(-50%, -100%)) lives in layout.css so
  // the .widget-help-label--hidden modifier can extend it with a fade-out
  // offset. Don't set transform inline here or it would beat the class.
  const style = {
    position: 'fixed',
    left: chipLeft,
    top: chipTop,
    background: bgRgba,
    backgroundColor: bgRgb,
    color: textColor,
    '--widget-help-color-rgb': bgRgb,
    '--widget-help-color-rgba': bgRgba,
    '--widget-help-arrow-dx': `${arrowDx}px`,
  };

  return createPortal(
    <div
      ref={pillRef}
      className={`widget-help-label${hidden ? ' widget-help-label--hidden' : ''}`}
      role="note"
      aria-label={t('label')}
      aria-hidden={hidden}
      style={style}
    >
      <span className="widget-help-label__text">{t('label')}</span>
    </div>,
    document.body
  );
};

export default WidgetHelpLabel;
